import { PageTransition } from '@/components/PageTransition'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Briefcase,
  Plus,
  Layers,
  MoreVertical,
  Trash2,
  Download,
  Sparkles,
  FileText,
  Search,
  Building2,
} from 'lucide-react'
import { useBusinessCases } from '@/contexts/BusinessCasesContext'
import { toast } from 'sonner'
import { downloadBusinessCasePptx } from '@/lib/pptx-generator'
import { substitutePptx } from '@/lib/pptx-substitutor'
import type { BusinessCase } from '@/types/business-cases'

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusBadge(status: BusinessCase['status']) {
  const map = {
    draft:     { label: 'Draft',     variant: 'secondary' },
    generated: { label: 'Generated', variant: 'default' },
    edited:    { label: 'Edited',    variant: 'default' },
    sent:      { label: 'Sent',      variant: 'outline' },
  } as const
  const cfg = map[status] ?? { label: status, variant: 'secondary' }
  return <Badge variant={cfg.variant as 'default' | 'secondary' | 'outline'}>{cfg.label}</Badge>
}

// ── Tracker View ──────────────────────────────────────────────────────────────

function TrackerView({ cases, onDownload, downloadingId }: {
  cases: BusinessCase[]
  onDownload: (bc: BusinessCase) => void
  downloadingId: string | null
}) {
  const [search, setSearch] = useState('')

  const filtered = search.trim()
    ? cases.filter((bc) => bc.company_name.toLowerCase().includes(search.toLowerCase()))
    : cases

  // Group by company name
  const grouped = filtered.reduce<Record<string, BusinessCase[]>>((acc, bc) => {
    const key = bc.company_name || 'Unknown Company'
    if (!acc[key]) acc[key] = []
    acc[key].push(bc)
    return acc
  }, {})

  const sortedCompanies = Object.keys(grouped).sort((a, b) => a.localeCompare(b))

  return (
    <div>
      {/* Search */}
      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por empresa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {cases.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Briefcase className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium">Sin business cases aún</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Business cases generated from the One Time Use section will appear here grouped by company.
            </p>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="mb-4 h-10 w-10 text-muted-foreground" />
            <h3 className="mb-1 text-base font-medium">Sin resultados para "{search}"</h3>
            <p className="text-sm text-muted-foreground">Try a different company name.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sortedCompanies.map((company) => {
            const companyCases = grouped[company]
            return (
              <div key={company}>
                {/* Company header */}
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold text-sm">{company}</h3>
                  <Badge variant="secondary" className="text-xs h-5">
                    {companyCases.length} {companyCases.length === 1 ? 'case' : 'cases'}
                  </Badge>
                </div>

                {/* Cases for this company */}
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 ml-6">
                  {companyCases.map((bc) => (
                    <Card key={bc.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            {bc.contact_name && (
                              <p className="text-sm font-medium line-clamp-1">{bc.contact_name}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                              {bc.template?.name ?? 'Unknown template'}
                            </p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="shrink-0 h-7 w-7">
                                <MoreVertical className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => onDownload(bc)}
                                disabled={downloadingId === bc.id}
                              >
                                <Download className="mr-2 h-4 w-4" />
                                {downloadingId === bc.id ? 'Generating...' : 'Download PPTX'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <div className="mt-2 flex items-center justify-between">
                          {statusBadge(bc.status)}
                          <span className="text-xs text-muted-foreground">
                            {new Date(bc.created_at).toLocaleDateString()}
                          </span>
                        </div>

                        {bc.signals_used && bc.signals_used.length > 0 && (
                          <div className="mt-2 border-t pt-2">
                            <div className="flex flex-wrap gap-1">
                              {bc.signals_used.slice(0, 2).map((s, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">
                                  {s.name}
                                </Badge>
                              ))}
                              {bc.signals_used.length > 2 && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                  +{bc.signals_used.length - 2}
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 w-full h-7 text-xs"
                          onClick={() => onDownload(bc)}
                          disabled={downloadingId === bc.id}
                        >
                          <Download className="mr-1.5 h-3.5 w-3.5" />
                          {downloadingId === bc.id ? 'Generating...' : 'Download PPTX'}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function BusinessCases() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isTracker = searchParams.get('view') === 'tracker'

  const { cases, templates, isLoadingCases, isLoadingTemplates, deleteCase, deleteTemplate, downloadPptxTemplate } = useBusinessCases()
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  if (isLoadingCases || isLoadingTemplates) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  // ── Download PPTX ──
  const handleDownload = async (bc: BusinessCase) => {
    if (!bc.template) {
      toast.error('Template data not available')
      return
    }
    setDownloadingId(bc.id)
    try {
      const content = { ...(bc.generated_content ?? {}), ...(bc.edited_content ?? {}) }
      if (bc.template.template_type === 'uploaded_pptx') {
        // Uploaded templates have slide_structure: [] — the pptxgenjs path
        // would emit a 0-slide file. Substitute {{variables}} into the uploaded
        // file instead (same flow as BusinessCaseGenerate.handleDownloadPptx).
        if (!bc.template.pptx_storage_path) throw new Error('Template file not available in storage')
        const templateBlob = await downloadPptxTemplate(bc.template.pptx_storage_path)
        const resultBlob = await substitutePptx(templateBlob, content)
        const url = URL.createObjectURL(resultBlob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${bc.company_name.replace(/[^a-zA-Z0-9 _-]/g, '')}_BusinessCase.pptx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } else {
        await downloadBusinessCasePptx(bc, bc.template, content)
      }
      toast.success('PPTX downloaded')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate PPTX')
    } finally {
      setDownloadingId(null)
    }
  }

  // ── Delete case ──
  const handleDeleteCase = async (id: string, name: string) => {
    if (!confirm(`Delete business case for "${name}"? This cannot be undone.`)) return
    try {
      await deleteCase(id)
      toast.success('Business case deleted')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  // ── Delete template ──
  const handleDeleteTemplate = async (id: string, name: string) => {
    if (!confirm(`Delete template "${name}"? All generated cases will remain but can no longer be traced back to this template.`)) return
    try {
      await deleteTemplate(id)
      toast.success('Template deleted')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  // ── Tracker view ──────────────────────────────────────────────────────────
  if (isTracker) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-[28px] font-bold tracking-tight font-heading">Business Cases</h1>
          <p className="text-muted-foreground">Search and consult business cases created by company</p>
        </div>
        <TrackerView cases={cases} onDownload={handleDownload} downloadingId={downloadingId} />
      </div>
    )
  }

  // ── Standard view ─────────────────────────────────────────────────────────
  return (
    <PageTransition className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-[28px] font-bold tracking-tight font-heading">Business Cases</h1>
          <p className="text-muted-foreground">
            AI-generated business cases personalized for each prospect
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/business-cases/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Template
          </Button>
          <Button onClick={() => navigate('/business-cases/generate')} disabled={templates.length === 0}>
            <Sparkles className="mr-2 h-4 w-4" />
            Generate Case
          </Button>
        </div>
      </div>

      <Tabs defaultValue="cases">
        <TabsList className="mb-6">
          <TabsTrigger value="cases">
            Cases Library
            {cases.length > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">{cases.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="templates">
            Templates
            {templates.length > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">{templates.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Cases Library ── */}
        <TabsContent value="cases">
          {cases.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FileText className="mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-medium">Sin business cases aún</h3>
                <p className="mb-6 text-sm text-muted-foreground text-center max-w-md">
                  {templates.length === 0
                    ? 'Start by creating a template, then generate personalized cases for your leads.'
                    : 'Select a template and a lead to generate your first personalized business case.'}
                </p>
                {templates.length === 0 ? (
                  <Button onClick={() => navigate('/business-cases/new')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create First Template
                  </Button>
                ) : (
                  <Button onClick={() => navigate('/business-cases/generate')}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Business Case
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {cases.map((bc) => (
                <Card key={bc.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 min-w-0">
                        <Briefcase className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-semibold line-clamp-1">{bc.company_name}</p>
                          {bc.contact_name && (
                            <p className="text-sm text-muted-foreground line-clamp-1">{bc.contact_name}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {bc.template?.name ?? 'Unknown template'}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleDownload(bc)}
                            disabled={downloadingId === bc.id}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            {downloadingId === bc.id ? 'Generating...' : 'Download PPTX'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDeleteCase(bc.id, bc.company_name)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      {statusBadge(bc.status)}
                      <span className="text-xs text-muted-foreground">
                        {new Date(bc.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Signals used */}
                    {bc.signals_used && bc.signals_used.length > 0 && (
                      <div className="mt-3 border-t pt-3">
                        <p className="text-xs text-muted-foreground font-medium mb-1">
                          Research signals used
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {bc.signals_used.slice(0, 3).map((s, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {s.name}
                            </Badge>
                          ))}
                          {bc.signals_used.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{bc.signals_used.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() => handleDownload(bc)}
                      disabled={downloadingId === bc.id}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {downloadingId === bc.id ? 'Generating PPTX...' : 'Download PPTX'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Templates ── */}
        <TabsContent value="templates">
          {templates.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Layers className="mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-medium">Sin plantillas aún</h3>
                <p className="mb-6 text-sm text-muted-foreground text-center max-w-md">
                  Create a template by describing the business case structure you need. The AI will generate the slide layout.
                </p>
                <Button onClick={() => navigate('/business-cases/new')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create First Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((t) => (
                <Card
                  key={t.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/business-cases/templates/${t.id}`)}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold line-clamp-1">{t.name}</p>
                        {t.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{t.description}</p>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          asChild
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/business-cases/generate?templateId=${t.id}`)
                            }}
                          >
                            <Sparkles className="mr-2 h-4 w-4" />
                            Generate Case
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteTemplate(t.id, t.name)
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {t.slide_structure.length} slides
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {t.source === 'ai_generated' ? 'AI Generated' : 'Uploaded'}
                      </Badge>
                      {!t.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                    </div>

                    <p className="text-xs text-muted-foreground mt-2">
                      Created {new Date(t.created_at).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageTransition>
  )
}
